import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Steps, Spin, Button, Divider, Space } from 'antd';
import { THEMES } from 'shared/constants/design';
import removeEmptyValues from 'shared/utils/removeEmptyValues';
import { useQuery, useMutation } from '@apollo/client';

import { Prompt } from 'react-router';
import { DEFAULT_DEVICE_VALUES } from 'shared/constants/distribution';
import Page from 'components/Page';
import { EyeOutlined, SettingOutlined } from '@ant-design/icons';
import { useSurveyPreviewPopup } from 'components/SurveyPreview';
import { dayjs } from 'shared/utils/date';
import useUserConfirmation from 'hooks/useUserConfirmation';
import useGlobalContext from 'hooks/useGlobalContext';
import { DeviceType, SurveySetting, SurveyType } from 'shared/prisma/types';
import SurveySelect from 'components/SurveySelect';
import query from './editSurvey.gql';
import mutation from './updateSurveyMutation.gql';
import AudienceStep from './AudienceStep';
import PublishStep from './PublishStep';
import DesignStep from './DesignStep';
import IntroStep from './IntroStep';
import EndingStep from './EndingStep';
import EditStructure from './EditStructure';
import AddTranslations from '../AddTranslations';
import ThemeStep from './ThemeStep';
import EditSurveyGroup from './EditSurveyGroup';

// Ant form prefers map
function getTranslatedMap(languages, getter) {
  return languages.reduce((acc, s) => ({
    ...acc,
    [s.id]: getter(s),
  }), {});
}

// Graphql doesnt support dynamic keys so convert to array
function getTranslatedArray(map, getter = (v) => v) {
  return map ? Object.entries(map).map(([surveyId, value]) => ({ surveyId, ...getter(value) })) : undefined;
}

function getTypeFromValues(dist) {
  const { container, trigger } = dist;

  if (container === 'embed') {
    return { type: 'embedded' };
  }

  if (trigger === 'existingButton') {
    return { type: 'existingButton' };
  }

  return { type: 'delayed' };
}

function getValuesFromType({ type }) {
  switch (type) {
    case 'embedded':
      return {
        container: 'embed',
        trigger: null,
      };
    case 'existingButton':
      return {
        container: null,
        trigger: 'existingButton',
      };
    default:
      return {
        trigger: null,
      };
  }
}

function getDeviceInputValues({ type, ...values } = {}) {
  return {
    ...values,
    ...getValuesFromType({ type }),
  };
}

function getDeviceValues(deviceType, { type, distributions }, settingName) {
  const dist = distributions.find(d => d.deviceTypes?.includes(deviceType));

  const setting = dist?.[settingName] || SurveySetting.Default;

  const values = {
    ...DEFAULT_DEVICE_VALUES[type][deviceType],
    ...(setting !== SurveySetting.Default ? removeEmptyValues(dist) : {}),
    setting,
    distributionId: dist?.id,
  };

  return {
    ...values,
    ...getTypeFromValues(values),
  };
}

function getThemeValues({ distributions }) {
  const dist = distributions?.find(d => d);

  const theme = dist?.theme || 'default';

  return dist ? {
    distributionId: dist.id,
    theme,
    customTheme: {
      ...THEMES[theme],
      ...(theme === 'custom' ? dist.customTheme : {}),
    },
  } : {};
}

function getPublishDetailsValues({ distributions }) {
  const dist = distributions?.find(d => d.websiteLanguageId || d.website?.id);

  if (dist) {
    return {
      distributionId: dist.id,
      websiteLanguageId: dist.websiteLanguageId || dist.website?.id,
    };
  }

  return {};
}

function getPublishValues({ distributions }) {
  const dist = distributions?.find(d => d);

  if (dist) {
    return {
      includedUrls: dist.includedUrls,
      excludedUrls: dist.excludedUrls,
      excludedIPs: dist.excludedIPs,
      groupSurveyIds: dist.groupSurveyIds,
      startDate: dist.startDate ? dayjs(dist.startDate) : null,
      endDate: dist.endDate ? dayjs(dist.endDate) : null,
      customExpression: dist.customExpression,
    };
  }

  return {};
}

function getInitialValues(survey) {
  let initialValues;
  let languages;
  let translations;

  if (survey) {
    const { settings } = survey;

    translations = survey.translations || [];
    languages = [survey, ...translations];

    initialValues = {
      intro: {
        welcomePageContent: getTranslatedMap(languages, s => s.settings.welcomePageContent),
        showWelcomePage: settings.showWelcomePage,
        hideIntroDisclaimer: settings.hideIntroDisclaimer,
      },
      ending: {
        thankYouPageContent: getTranslatedMap(languages, s => s.settings.thankYouPageContent),
        clientPanel: settings.clientPanel,
      },
      audience: getTranslatedMap(languages, (s) => ({
        mobile: getDeviceValues(DeviceType.Mobile, s, 'behaviorSetting'),
        tablet: getDeviceValues(DeviceType.Tablet, s, 'behaviorSetting'),
        desktop: getDeviceValues(DeviceType.Desktop, s, 'behaviorSetting'),
      })),
      design: getTranslatedMap(languages, (s) => ({
        mobile: getDeviceValues(DeviceType.Mobile, s, 'designSetting'),
        tablet: getDeviceValues(DeviceType.Tablet, s, 'designSetting'),
        desktop: getDeviceValues(DeviceType.Desktop, s, 'designSetting'),
      })),
      theme: getThemeValues(survey),
      publish: {
        ...getPublishValues(survey),
        publishDetails: getTranslatedMap(languages, getPublishDetailsValues),
      },
    };
  }
  return { initialValues, languages, translations };
}

function getInputValues(values) {
  const { intro, ending, design, theme, audience, publish } = values;

  return {
    intro: intro ? {
      ...intro,
      welcomePageContent: getTranslatedArray(intro.welcomePageContent, v => ({ text: v })),
    } : undefined,
    ending: ending ? {
      ...ending,
      thankYouPageContent: getTranslatedArray(ending.thankYouPageContent, v => ({ text: v })),
    } : undefined,
    design: getTranslatedArray(design),
    theme,
    audience: getTranslatedArray(audience, value => ({
      mobile: value.mobile ? getDeviceInputValues(value.mobile) : undefined,
      tablet: value.tablet ? getDeviceInputValues(value.tablet) : undefined,
      desktop: value.desktop ? getDeviceInputValues(value.desktop) : undefined,
    })),
    publish: publish ? {
      ...publish,
      publishDetails: getTranslatedArray(publish.publishDetails),
    } : undefined,
  };
}

const EditSurvey = ({ surveyId, onSurveySelect }) => {
  const [current, setCurrent] = useState(0);
  const [isTouched, setIsTouched] = useState(false);
  const [addTranslation, setAddTranslation] = useState(false);
  const [form] = Form.useForm();
  const surveyPreviewPopup = useSurveyPreviewPopup();
  const getUserConfirmation = useUserConfirmation();
  const { message } = useGlobalContext();
  const [editSurveyGroup, setEditSurveyGroup] = useState(false);

  const { loading, data: { survey } = {}, refetch } = useQuery(query, {
    variables: { id: surveyId },
  });

  const [updateSurvey, { loading: mutationLoading }] = useMutation(mutation);

  useEffect(() => {
    setIsTouched(false);
    form.resetFields();
  }, [survey, current]);

  useEffect(() => {
    setCurrent(0);
  }, [survey?.type]);

  // useEffect(() => {
  //   if (visible) {
  //     refetch();
  //   }
  // }, [visible]);

  const { initialValues, languages, translations } = useMemo(() => getInitialValues(survey), [survey]);

  const stepProps = {
    survey,
    translations,
    languages,
    refetch,
    initialValues,
  };

  const contentSteps = [];

  if (survey?.type !== SurveyType.Feedback) {
    contentSteps.push({
      title: 'Intro',
      content: <IntroStep {...stepProps} name="intro" />,
    });
  }

  contentSteps.push({
    title: 'Questions',
    content: <EditStructure {...stepProps} />,
  }, {
    title: 'Ending',
    content: <EndingStep {...stepProps} name="ending" />,
  });

  const settingsSteps = [];

  if (survey?.type !== SurveyType.Cx) {
    settingsSteps.push({
      title: 'Behavior',
      content: <AudienceStep {...stepProps} name="audience" />,
    });
  }

  settingsSteps.push({
    title: 'Appearance',
    content: <DesignStep {...stepProps} name="design" />,
  }, {
    title: 'Theme',
    content: <ThemeStep {...stepProps} name="theme" />,
  }, {
    title: 'Publish',
    content: <PublishStep {...stepProps} name="publish" />,
  });

  const allSteps = [...contentSteps, ...settingsSteps];

  allSteps.forEach((step, index) => {
    step.status = index === current ? 'process' : 'wait';
    step.className = step.title.toLowerCase();
  });

  const changeStep = (step) => {
    if (isTouched) {
      getUserConfirmation('You have unsaved changes. Are you sure you want to leave?', ok => { if (ok) setCurrent(step); });
    } else {
      setCurrent(step);
    }
  };

  const onFinish = async (values) => {
    try {
      await updateSurvey({
        variables: {
          id: surveyId,
          input: getInputValues(values),
        },
      });
      setIsTouched(false);
      await refetch();
      message.success('Survey updated successfully');
    } catch (e) {
      console.log(e);
      message.error('Something went wrong, try again later');
    }
  };

  const onFieldsChange = (changedFields) => {
    changedFields.forEach(f => {
      if (f.name[f.name.length - 1] === 'theme' && f.value !== 'custom') {
        form.setFieldsValue({
          theme: {
            customTheme: THEMES[f.value],
          },
        });
      }

      if (f.name.includes('setting') && f.value === SurveySetting.Default) {
        if (f.name.includes('audience')) {
          if (f.name.includes('desktop')) {
            form.setFieldsValue({
              audience: {
                [f.name[1]]: {
                  desktop: DEFAULT_DEVICE_VALUES[survey.type][DeviceType.Desktop],
                },
              },
            });
          }
          if (f.name.includes('tablet')) {
            form.setFieldsValue({
              audience: {
                [f.name[1]]: {
                  tablet: DEFAULT_DEVICE_VALUES[survey.type][DeviceType.Tablet],
                },
              },
            });
          }
          if (f.name.includes('mobile')) {
            form.setFieldsValue({
              audience: {
                [f.name[1]]: {
                  mobile: DEFAULT_DEVICE_VALUES[survey.type][DeviceType.Mobile],
                },
              },
            });
          }
        }
        if (f.name.includes('design')) {
          if (f.name.includes('desktop')) {
            form.setFieldsValue({
              design: {
                [f.name[1]]: {
                  desktop: DEFAULT_DEVICE_VALUES[survey.type][DeviceType.Desktop],
                },
              },
            });
          }
          if (f.name.includes('tablet')) {
            form.setFieldsValue({
              design: {
                [f.name[1]]: {
                  tablet: DEFAULT_DEVICE_VALUES[survey.type][DeviceType.Tablet],
                },
              },
            });
          }
          if (f.name.includes('mobile')) {
            form.setFieldsValue({
              design: {
                [f.name[1]]: {
                  mobile: DEFAULT_DEVICE_VALUES[survey.type][DeviceType.Mobile],
                },
              },
            });
          }
        }
      }

      if (f.name.includes('type') && f.value === 'delayed') {
        form.setFieldsValue({
          audience: {
            [f.name[1]]: {
              [f.name[2]]: {
                container: DEFAULT_DEVICE_VALUES[survey.type][f.name[2].toUpperCase()].container,
              },
            },
          },
        });
      }
    });
  };

  const onValuesChange = () => {
    setIsTouched(true);
  };

  const onTranslationAdded = () => {
    refetch();
    setAddTranslation(false);
  };

  const onEditSurveyGroup = () => {
    refetch();
    setEditSurveyGroup(false);
  };

  return (
    <Page>
      <Page.Header
        title={(
          <Space>
            Edit survey
            <SurveySelect primary surveyGroupId={survey?.languageParent.id} value={surveyId} onChange={onSurveySelect} />
          </Space>
        )}
        extra={[survey?.distributions.length > 0 && (
          <Button key="preview" icon={<EyeOutlined />} onClick={() => surveyPreviewPopup.open(survey.distributions[0].id)}>Preview</Button>
        ), <Button type="primary" key="settings" icon={<SettingOutlined />} onClick={() => setEditSurveyGroup(true)}>Settings</Button>]}
      />

      <Page.Content>
        <Spin spinning={loading}>
          <Form
            requiredMark={false}
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFieldsChange={onFieldsChange}
            onValuesChange={onValuesChange}
            onFinish={onFinish}
          >
            <Prompt when={isTouched} message="You have unsaved changes. Are you sure you want to leave?" />
            <Row gutter={16}>
              <Col span={4}>
                <div style={{ position: 'sticky', top: 16 }}>
                  <Divider orientation="center">
                    Content
                  </Divider>
                  <Steps direction="vertical" current={current} onChange={changeStep} items={contentSteps} />
                  <Divider orientation="center">
                    Settings
                  </Divider>
                  <Steps direction="vertical" current={current} onChange={changeStep} initial={contentSteps.length} items={settingsSteps} />
                </div>
              </Col>
              <Col span={20}>
                {survey && allSteps[current].content}
              </Col>
            </Row>
          </Form>
        </Spin>
        <Page.Footer sticky>
          <Button type="primary" onClick={form.submit} loading={mutationLoading} disabled={!isTouched}>Save</Button>
        </Page.Footer>
      </Page.Content>

      <AddTranslations surveyId={surveyId} open={addTranslation} onCancel={() => setAddTranslation(false)} onDone={onTranslationAdded} exclude={languages?.map(l => l.language)} />
      {survey && <EditSurveyGroup open={editSurveyGroup} onCancel={() => setEditSurveyGroup(false)} onDone={onEditSurveyGroup} survey={survey} />}
    </Page>
  );
};

export default EditSurvey;
